import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { AlarmClock, Eraser, X } from "lucide-react";

const DAYS = [["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];

function TimeBox({ value, onChange, testid, strong }) {
  return (
    <div className="relative w-[78px]">
      <input type="time" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        lang="en-GB" className={`h-8 w-full rounded-md border px-1 text-xs ${value ? (strong ? "border-brand bg-brand/5 font-semibold" : "border-brand/60 bg-orange-50/60") : "border-slate-200 text-slate-400"}`} />
      {value && (
        <button type="button" onClick={() => onChange("")} title="Clear" aria-label="Clear time" data-testid={`${testid}-x`}
          className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-slate-700 text-white flex items-center justify-center hover:bg-red-600 transition-colors">
          <X className="h-2.5 w-2.5" />
        </button>
      )}
    </div>
  );
}

function Row({ label, every, days, onEvery, onDay, onClear, testid }) {
  return (
    <tr className="border-t border-slate-100" data-testid={testid}>
      <td className="py-1.5 pr-2 text-xs font-medium text-slate-700 max-w-[160px]">
        <div className="flex items-center gap-1">
          <span className="truncate">{label}</span>
          {(every || Object.keys(days || {}).length > 0) && <Button type="button" size="sm" variant="ghost" className="h-6 px-1.5 text-[11px] text-slate-500 hover:text-red-600 shrink-0" title="Clear row" onClick={onClear} data-testid={`${testid}-clear`}><Eraser className="h-3 w-3 mr-0.5" />Clear</Button>}
        </div>
      </td>
      <td className="py-1.5 pr-2"><TimeBox value={every} onChange={onEvery} testid={`${testid}-every`} strong /></td>
      {DAYS.map(([k]) => <td key={k} className="py-1.5 pr-1"><TimeBox value={days?.[k]} onChange={(v) => onDay(k, v)} testid={`${testid}-${k}`} /></td>)}
    </tr>
  );
}

const setKey = (obj, k, v) => { const n = { ...(obj || {}) }; if (v) n[k] = v; else delete n[k]; return n; };

export function EarliestClockIn({ form, setForm, cleaners }) {
  const siteCleaners = cleaners.filter((w) => (form.assignedCleanerIds || []).includes(w.id) || (form.accessOnlyCleanerIds || []).includes(w.id));
  const byC = form.earliestClockInByCleaner || {};
  const byCD = form.earliestClockInByCleanerDays || {};
  const setCleanerDay = (id, k, v) => {
    const days = setKey(byCD[id], k, v);
    const next = { ...byCD, [id]: days };
    if (!Object.keys(days).length) delete next[id];
    setForm({ ...form, earliestClockInByCleanerDays: next });
  };
  const clearCleaner = (id) => {
    const d = { ...byCD }; delete d[id];
    setForm({ ...form, earliestClockInByCleaner: setKey(byC, id, ""), earliestClockInByCleanerDays: d });
  };
  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="site-earliest-clockin">
      <Label className="flex items-center gap-1.5"><AlarmClock className="h-4 w-4 text-brand" />Earliest clock-in (UK time)</Label>
      <p className="text-[11px] text-slate-400">Cleaners trying to clock in earlier are blocked and told when they can. A day box beats the "Every day" box, and a cleaner's own row beats the whole-site row. Leave everything blank for no limit.</p>
      <div className="overflow-x-auto max-w-full">
        <table className="text-left mt-1.5">
          <thead><tr className="text-[11px] uppercase tracking-wide text-slate-500">
            <th className="pr-2 py-1 font-semibold" /><th className="pr-2 py-1 font-semibold">Every day</th>
            {DAYS.map(([k, l]) => <th key={k} className="pr-1 py-1 font-semibold">{l}</th>)}
          </tr></thead>
          <tbody>
            <Row label="Whole site" testid="site-earliest-site" every={form.earliestClockIn} days={form.earliestClockInDays}
              onEvery={(v) => setForm({ ...form, earliestClockIn: v })}
              onDay={(k, v) => setForm({ ...form, earliestClockInDays: setKey(form.earliestClockInDays, k, v) })}
              onClear={() => setForm({ ...form, earliestClockIn: "", earliestClockInDays: {} })} />
            {siteCleaners.map((w) => (
              <Row key={w.id} label={w.displayName || w.name} testid={`site-earliest-c-${w.id}`} every={byC[w.id]} days={byCD[w.id]}
                onEvery={(v) => setForm({ ...form, earliestClockInByCleaner: setKey(byC, w.id, v) })}
                onDay={(k, v) => setCleanerDay(w.id, k, v)} onClear={() => clearCleaner(w.id)} />
            ))}
          </tbody>
        </table>
      </div>
      {!siteCleaners.length && <p className="text-[11px] text-slate-400">Add cleaners to this site above to give them their own times.</p>}
    </div>
  );
}
